import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { ScrollArea } from './ui/scroll-area';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import NodeComments from './NodeComments';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  X, ArrowRight, ArrowLeftRight, Server, CheckCircle2, XCircle,
  Trash2, BookOpen, Layers, Plus, Zap, Loader2, Edit2, Cable, Lightbulb, Copy,
  Building2, MapPin, ArrowDownToLine, AlertTriangle, Wand2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LAYER_META = {
  actual: { label: 'Actual', color: 'bg-emerald-600', textColor: 'text-emerald-700', bgLight: 'bg-emerald-50 dark:bg-emerald-950/20' },
  possible: { label: 'Possible', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 dark:bg-amber-950/20' },
  ideal: { label: 'Ideal', color: 'bg-blue-600', textColor: 'text-blue-700', bgLight: 'bg-blue-50 dark:bg-blue-950/20' },
};

export default function RightPanel({
  selectedNode,
  stackData,
  selectedProperty,
  onClose,
  role,
  onDeleteIntegration,
  onNavigateToDatabase,
  isMobile,
  activeLayer,
  token,
  onRefreshStack,
  width,
  onQuickAddIntegration,
}) {
  const [layerAssignments, setLayerAssignments] = useState([]);
  const [showAddLayer, setShowAddLayer] = useState(false);
  const [addLayerForm, setAddLayerForm] = useState({ layer: 'possible', product_id: '', vendor_name: '' });
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [quickFillOpen, setQuickFillOpen] = useState(false);
  const [quickFillProducts, setQuickFillProducts] = useState([]);
  const [quickFillLoading, setQuickFillLoading] = useState(false);
  const [quickFillSaving, setQuickFillSaving] = useState(false);
  const [editAssignOpen, setEditAssignOpen] = useState(false);
  const [editAssignProducts, setEditAssignProducts] = useState([]);
  const [editAssignLoading, setEditAssignLoading] = useState(false);

  // Property-level integrations state
  const [propIntegrations, setPropIntegrations] = useState([]);
  const [propIntegSuggestions, setPropIntegSuggestions] = useState([]);
  const [showAddPropInteg, setShowAddPropInteg] = useState(false);
  const [customFromName, setCustomFromName] = useState('');
  const [customToName, setCustomToName] = useState('');
  const [addFromProductId, setAddFromProductId] = useState('');
  const [addToProductId, setAddToProductId] = useState('');
  const [deployedProducts, setDeployedProducts] = useState([]);

  // Quick-add integration
  const [quickAddMode, setQuickAddMode] = useState(null); // null, 'two-way', 'one-way', 'one-way-reverse'
  const [showManualInteg, setShowManualInteg] = useState(false);
  const [manualIntegTarget, setManualIntegTarget] = useState('');
  const [manualIntegDirection, setManualIntegDirection] = useState('two-way');

  // Property-level quick add integration
  const [propQuickAddMode, setPropQuickAddMode] = useState(null);

  // Fix missing product on a property integration
  const [fixProdOpen, setFixProdOpen] = useState(false);
  const [fixProdInteg, setFixProdInteg] = useState(null); // integration record
  const [fixProdSide, setFixProdSide] = useState('from'); // 'from' | 'to'
  const [fixProdOptions, setFixProdOptions] = useState([]);
  const [fixProdSelected, setFixProdSelected] = useState('');
  const [fixProdLoading, setFixProdLoading] = useState(false);
  const [fixProdSaving, setFixProdSaving] = useState(false);

  // Modal for missing product info when copying integration down
  const [showCopyIntegModal, setShowCopyIntegModal] = useState(false);
  const [copyIntegForm, setCopyIntegForm] = useState({ fromPlatformId: '', toPlatformId: '', fromName: '', toName: '', fromProductId: '', toProductId: '', direction: 'two-way' });

  // Platform assignment card state (overall view)
  const [showPlatformCard, setShowPlatformCard] = useState(false);
  const [platformProducts, setPlatformProducts] = useState([]);
  const [platformAssignments, setPlatformAssignments] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [platformCardLoading, setPlatformCardLoading] = useState(false);
  const [assigningProp, setAssigningProp] = useState(null);

  const isAdmin = role === 'admin';
  const isPropertyView = selectedProperty && selectedProperty !== 'overall';
  const headers = useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  // Copy a platform integration down to property level
  const copyDownToProperty = async (fromPlatformId, toPlatformId, direction) => {
    if (!isPropertyView) return;
    const fromNode = (stackData?.nodes || []).find(n => n.id === fromPlatformId);
    const toNode = (stackData?.nodes || []).find(n => n.id === toPlatformId);
    const fromProduct = fromNode?.product_name || fromNode?.vendor_name || '';
    const toProduct = toNode?.product_name || toNode?.vendor_name || '';
    const fromProdId = fromNode?.solutions?.[0]?.product_id || '';
    const toProdId = toNode?.solutions?.[0]?.product_id || '';

    // If either side is missing product info, show modal
    if ((!fromProdId && !fromProduct) || (!toProdId && !toProduct)) {
      setCopyIntegForm({
        fromPlatformId, toPlatformId,
        fromName: fromProduct || fromNode?.name || fromPlatformId,
        toName: toProduct || toNode?.name || toPlatformId,
        fromProductId: fromProdId, toProductId: toProdId,
        direction: direction || 'two-way',
      });
      setShowCopyIntegModal(true);
      return;
    }

    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/property-integrations`, {
        property_id: selectedProperty,
        from_product_id: fromProdId,
        from_product_name: fromProduct,
        from_platform_id: fromPlatformId,
        to_product_id: toProdId,
        to_product_name: toProduct,
        to_platform_id: toPlatformId,
        direction: direction || 'two-way',
        status: 'active',
      }, { headers });
      if (res.data?.status === 'exists') {
        toast.info('Already exists at property level');
      } else {
        toast.success(`Copied to property: ${fromProduct || fromPlatformId} \u2194 ${toProduct || toPlatformId}`);
      }
      fetchPropIntegrations();
      if (onRefreshStack) onRefreshStack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to copy');
    }
  };

  const submitCopyIntegForm = async () => {
    const { fromPlatformId, toPlatformId, fromName, toName, fromProductId, toProductId, direction } = copyIntegForm;
    if ((!fromName && !fromProductId) || (!toName && !toProductId)) {
      toast.error('Please provide names for both systems');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/property-integrations`, {
        property_id: selectedProperty,
        from_product_id: fromProductId || '',
        from_product_name: fromName,
        from_platform_id: fromPlatformId,
        to_product_id: toProductId || '',
        to_product_name: toName,
        to_platform_id: toPlatformId,
        direction: direction || 'two-way',
        status: 'active',
      }, { headers });
      toast.success(`Integration added: ${fromName} \u2194 ${toName}`);
      setShowCopyIntegModal(false);
      fetchPropIntegrations();
      if (onRefreshStack) onRefreshStack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // Quick-add integration: user picks direction, then clicks a node on the map
  const startQuickAdd = (direction) => {
    setQuickAddMode(direction);
    if (onQuickAddIntegration) {
      onQuickAddIntegration({
        sourceNode: selectedNode,
        direction,
        onComplete: async (targetNode) => {
          try {
            const fromId = direction === 'one-way-reverse' ? targetNode.id : selectedNode.id;
            const toId = direction === 'one-way-reverse' ? selectedNode.id : targetNode.id;
            const dir = direction === 'one-way-reverse' ? 'one-way' : direction;
            await axios.post(`${BACKEND_URL}/api/admin/integrations`, {
              from_platform_id: fromId,
              to_platform_id: toId,
              direction: dir,
              label: '',
            }, { headers });
            toast.success(`Integration: ${selectedNode.short || selectedNode.name} ${dir === 'two-way' ? '↔' : '→'} ${targetNode.short || targetNode.name}`);
            if (onRefreshStack) onRefreshStack();
            fetchPropIntegrations();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create integration');
          }
          setQuickAddMode(null);
        },
      });
    }
  };

  // Property-level quick-add: pick direction, then click a node on the map to create
  // a property-level integration between the currently selected platform node (source)
  // and the clicked target platform node. Reuses copyDownToProperty which knows how to
  // resolve product info from the stackData or fall back to the "specify systems" modal.
  const startPropQuickAdd = (direction) => {
    if (!isPropertyView || !selectedNode) return;
    setPropQuickAddMode(direction);
    if (onQuickAddIntegration) {
      onQuickAddIntegration({
        sourceNode: selectedNode,
        direction,
        onComplete: async (targetNode) => {
          const fromId = direction === 'one-way-reverse' ? targetNode.id : selectedNode.id;
          const toId = direction === 'one-way-reverse' ? selectedNode.id : targetNode.id;
          const dir = direction === 'one-way-reverse' ? 'one-way' : direction;
          await copyDownToProperty(fromId, toId, dir);
          setPropQuickAddMode(null);
        },
      });
    }
  };

  // Open the "fix missing product" picker for one side of a property integration
  const openFixProduct = async (integ, side) => {
    const platformId = side === 'from' ? integ.from_platform_id : integ.to_platform_id;
    if (!platformId) {
      toast.error('No platform recorded on this side — recreate the integration instead.');
      return;
    }
    setFixProdInteg(integ);
    setFixProdSide(side);
    setFixProdSelected('');
    setFixProdOpen(true);
    setFixProdLoading(true);
    try {
      // Prefer products actually deployed at this property for the target platform,
      // fall back to all catalog products for that platform.
      const deployedForPlatform = deployedProducts.filter(p => p.platform === platformId);
      if (deployedForPlatform.length > 0) {
        setFixProdOptions(deployedForPlatform.map(p => ({ id: p.id, name: p.name, deployed: true })));
      } else {
        const r = await axios.get(`${BACKEND_URL}/api/products`, { headers, params: { platform_id: platformId, limit: 200 } });
        const all = (r.data.products || []).map(p => ({ id: p.id, name: p.name, deployed: false }));
        setFixProdOptions(all);
      }
    } catch {
      setFixProdOptions([]);
    } finally {
      setFixProdLoading(false);
    }
  };

  const submitFixProduct = async () => {
    if (!fixProdInteg || !fixProdSelected) return;
    const chosen = fixProdOptions.find(o => o.id === fixProdSelected);
    if (!chosen) return;
    setFixProdSaving(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/admin/property-integrations/${fixProdInteg.id}`, {
        side: fixProdSide,
        product_id: chosen.id,
        product_name: chosen.name,
      }, { headers });
      toast.success(`Product set: ${chosen.name}`);
      setFixProdOpen(false);
      setFixProdInteg(null);
      setFixProdSelected('');
      fetchPropIntegrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setFixProdSaving(false);
    }
  };

  // Manual add: pick target from dropdown
  const addManualIntegration = async () => {
    if (!manualIntegTarget || !selectedNode) return;
    try {
      const fromId = manualIntegDirection === 'one-way-reverse' ? manualIntegTarget : selectedNode.id;
      const toId = manualIntegDirection === 'one-way-reverse' ? selectedNode.id : manualIntegTarget;
      const dir = manualIntegDirection === 'one-way-reverse' ? 'one-way' : manualIntegDirection;
      await axios.post(`${BACKEND_URL}/api/admin/integrations`, {
        from_platform_id: fromId,
        to_platform_id: toId,
        direction: dir,
        label: '',
      }, { headers });
      const targetNode = (stackData?.nodes || []).find(n => n.id === manualIntegTarget);
      toast.success(`Integration: ${selectedNode.short} ${dir === 'two-way' ? '↔' : '→'} ${targetNode?.short || manualIntegTarget}`);
      setShowManualInteg(false);
      setManualIntegTarget('');
      if (onRefreshStack) onRefreshStack();
      fetchPropIntegrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // Open the platform assignment card
  const openPlatformCard = async () => {
    setShowPlatformCard(true);
    setPlatformCardLoading(true);
    try {
      const [prodRes, assignRes, propRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products`, { headers, params: { platform_id: selectedNode.id, limit: 100 } }),
        axios.get(`${BACKEND_URL}/api/assignments`, { params: { platform_type_id: selectedNode.id } }),
        axios.get(`${BACKEND_URL}/api/properties?show_all=true`, { headers }),
      ]);
      setPlatformProducts(prodRes.data.products || []);
      const assigns = Array.isArray(assignRes.data) ? assignRes.data : assignRes.data.assignments || [];
      setPlatformAssignments(assigns);
      const props = Array.isArray(propRes.data) ? propRes.data : propRes.data.properties || [];
      setAllProperties(props);
    } catch { setPlatformProducts([]); setPlatformAssignments([]); setAllProperties([]); }
    finally { setPlatformCardLoading(false); }
  };

  const assignPropertyFromCard = async (propertyId, productId) => {
    setAssigningProp(propertyId);
    const product = platformProducts.find(p => p.id === productId);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/assignments`, {
        property_id: propertyId,
        platform_type_id: selectedNode.id,
        product_id: productId || '',
        vendor_name: product?.vendor_name || product?.name || '',
        layer: 'actual',
      }, { headers });
      toast.success('Property assigned');
      // Refresh assignments
      const res = await axios.get(`${BACKEND_URL}/api/assignments`, { headers, params: { platform_type_id: selectedNode.id } });
      setPlatformAssignments(Array.isArray(res.data) ? res.data : res.data.assignments || []);
      if (onRefreshStack) onRefreshStack();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setAssigningProp(null); }
  };

  const removeAssignmentFromCard = async (assignmentId) => {
    if (!window.confirm('Remove this property assignment?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/assignments/${assignmentId}`, { headers });
      toast.success('Assignment removed');
      const res = await axios.get(`${BACKEND_URL}/api/assignments`, { headers, params: { platform_type_id: selectedNode.id } });
      setPlatformAssignments(Array.isArray(res.data) ? res.data : res.data.assignments || []);
      if (onRefreshStack) onRefreshStack();
    } catch { toast.error('Failed to remove'); }
  };

  // Fetch layer assignments when node changes in property view
  const fetchLayerAssignments = useCallback(async () => {
    if (!isPropertyView || !selectedNode || !token) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/assignments/layers`, {
        headers,
        params: { property_id: selectedProperty, platform_type_id: selectedNode.id },
      });
      setLayerAssignments(res.data.assignments || []);
    } catch {
      setLayerAssignments([]);
    }
  }, [isPropertyView, selectedNode, selectedProperty, token, headers]);

  useEffect(() => {
    fetchLayerAssignments();
  }, [fetchLayerAssignments]);

  // Fetch products for the platform when adding a layer assignment
  const fetchProducts = useCallback(async () => {
    if (!selectedNode || !showAddLayer) return;
    setLoadingProducts(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/products`, {
        headers,
        params: { platform_id: selectedNode.id, limit: 200 },
      });
      setProducts(res.data.products || []);
    } catch {
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  }, [selectedNode, showAddLayer, headers]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Fetch property-level integrations
  const fetchPropIntegrations = useCallback(async () => {
    if (!isPropertyView || !token) return;
    try {
      const [integRes, suggestRes, assignRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/property-integrations/${selectedProperty}`, { headers }),
        axios.get(`${BACKEND_URL}/api/property-integrations/${selectedProperty}/suggestions`, { headers }),
        axios.get(`${BACKEND_URL}/api/assignments/layers`, { headers, params: { property_id: selectedProperty } }).catch(() => ({ data: { assignments: [] } })),
      ]);
      setPropIntegrations(integRes.data.integrations || []);
      setPropIntegSuggestions(suggestRes.data.suggestions || []);
      // Build list of all products deployed at this property for the add dialog
      const prods = (assignRes.data.assignments || [])
        .filter(a => a.product_id && a.product_name)
        .map(a => ({ id: a.product_id, name: a.product_name, platform: a.platform_type_id }));
      // Deduplicate
      const seen = new Set();
      const unique = prods.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; });
      setDeployedProducts(unique);
    } catch {
      setPropIntegrations([]);
      setPropIntegSuggestions([]);
    }
  }, [isPropertyView, selectedProperty, token, headers]);

  useEffect(() => {
    fetchPropIntegrations();
  }, [fetchPropIntegrations]);

  // Add a property integration from suggestion
  const addPropIntegFromSuggestion = async (sug) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/property-integrations`, {
        property_id: selectedProperty,
        from_product_id: sug.from_product_id,
        from_product_name: sug.from_product_name,
        from_platform_id: sug.from_platform_id,
        to_product_id: sug.to_product_id,
        to_product_name: sug.to_product_name,
        to_platform_id: sug.to_platform_id,
        direction: sug.direction || 'two-way',
        status: 'active',
      }, { headers });
      toast.success(`Added: ${sug.from_product_name} ↔ ${sug.to_product_name}`);
      fetchPropIntegrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add integration');
    }
  };

  // Add a custom property integration
  const addCustomPropInteg = async () => {
    const fromProd = deployedProducts.find(p => p.id === addFromProductId);
    const toProd = deployedProducts.find(p => p.id === addToProductId);
    const fromName = fromProd?.name || customFromName;
    const toName = toProd?.name || customToName;
    if (!fromName || !toName) { toast.error('Enter names for both systems'); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/admin/property-integrations`, {
        property_id: selectedProperty,
        from_product_id: addFromProductId || '',
        from_product_name: fromName,
        from_platform_id: fromProd?.platform || '',
        to_product_id: addToProductId || '',
        to_product_name: toName,
        to_platform_id: toProd?.platform || '',
        direction: 'two-way',
        status: 'active',
      }, { headers });
      toast.success(`Added: ${fromName} ↔ ${toName}`);
      setShowAddPropInteg(false);
      setCustomFromName(''); setCustomToName('');
      setAddFromProductId(''); setAddToProductId('');
      fetchPropIntegrations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    }
  };

  // Remove a property integration
  const removePropInteg = async (integId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/property-integrations/${integId}`, { headers });
      toast.success('Integration removed');
      fetchPropIntegrations();
    } catch { toast.error('Failed to remove'); }
  };

  // Copy all suggestions at once
  const copyAllSuggestions = async () => {
    let added = 0;
    for (const sug of propIntegSuggestions) {
      try {
        await axios.post(`${BACKEND_URL}/api/admin/property-integrations`, {
          property_id: selectedProperty,
          from_product_id: sug.from_product_id,
          from_product_name: sug.from_product_name,
          from_platform_id: sug.from_platform_id,
          to_product_id: sug.to_product_id,
          to_product_name: sug.to_product_name,
          to_platform_id: sug.to_platform_id,
          direction: sug.direction || 'two-way',
          status: 'active',
        }, { headers });
        added++;
      } catch { /* skip duplicates */ }
    }
    toast.success(`Copied ${added} integration(s) from catalog`);
    fetchPropIntegrations();
  };

  if (!selectedNode) {
    if (isMobile) return null;
    return (
      <div style={{ width: `${width || 300}px` }} className="border-l bg-card/60 backdrop-blur shrink-0 flex items-center justify-center no-print">
        <div className="text-center px-6">
          <Server className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">Click a platform node to see details</p>
        </div>
      </div>
    );
  }

  // Find integrations connected to this node
  const integrations = (stackData?.links || []).filter(l => {
    const sid = l.source?.id || l.source;
    const tid = l.target?.id || l.target;
    return sid === selectedNode.id || tid === selectedNode.id;
  });

  const inbound = integrations.filter(l => {
    const tid = l.target?.id || l.target;
    return tid === selectedNode.id;
  });

  const outbound = integrations.filter(l => {
    const sid = l.source?.id || l.source;
    return sid === selectedNode.id;
  });

  const getNodeName = (id) => {
    const node = stackData?.nodes?.find(n => n.id === id);
    return node?.short || node?.name || id;
  };

  // Layer assignment helpers
  const existingLayers = layerAssignments.map(a => a.layer || 'actual');
  const missingLayers = ['actual', 'possible', 'ideal'].filter(l => !existingLayers.includes(l));

  const handleAddLayerAssignment = async () => {
    try {
      const selectedProduct = products.find(p => p.id === addLayerForm.product_id);
      await axios.post(`${BACKEND_URL}/api/admin/assignments`, {
        property_id: selectedProperty,
        platform_type_id: selectedNode.id,
        vendor_name: selectedProduct?.vendor_name || addLayerForm.vendor_name || '',
        product_id: addLayerForm.product_id || '',
        layer: addLayerForm.layer,
        notes: '',
      }, { headers });
      toast.success(`Added to ${LAYER_META[addLayerForm.layer]?.label || addLayerForm.layer} layer`);
      setShowAddLayer(false);
      setAddLayerForm({ layer: 'possible', product_id: '', vendor_name: '' });
      fetchLayerAssignments();
      if (onRefreshStack) onRefreshStack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add assignment');
    }
  };

  const handleRemoveLayerAssignment = async (layer) => {
    if (!window.confirm(`Remove from ${LAYER_META[layer]?.label || layer} layer?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/assignments`, {
        headers,
        params: { property_id: selectedProperty, platform_type_id: selectedNode.id, layer },
      });
      toast.success(`Removed from ${LAYER_META[layer]?.label || layer} layer`);
      fetchLayerAssignments();
      if (onRefreshStack) onRefreshStack();
    } catch (err) {
      toast.error('Failed to remove assignment');
    }
  };

  const openQuickFill = async () => {
    setQuickFillOpen(true);
    setQuickFillLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/products`, {
        headers, params: { platform_id: selectedNode.id, limit: 20 },
      });
      setQuickFillProducts(res.data.products || []);
    } catch { setQuickFillProducts([]); }
    finally { setQuickFillLoading(false); }
  };

  const openEditAssignment = async () => {
    setEditAssignOpen(true);
    setEditAssignLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/products`, {
        headers, params: { platform_id: selectedNode.id, limit: 30 },
      });
      setEditAssignProducts(res.data.products || []);
    } catch { setEditAssignProducts([]); }
    finally { setEditAssignLoading(false); }
  };

  const handleReassign = async (product) => {
    try {
      // Remove existing assignment(s) for this platform at this property first
      await axios.delete(`${BACKEND_URL}/api/admin/assignments`, {
        headers, params: { property_id: selectedProperty, platform_type_id: selectedNode.id, layer: 'actual' },
      });
      // Create the new assignment
      await axios.post(`${BACKEND_URL}/api/admin/assignments`, {
        property_id: selectedProperty,
        platform_type_id: selectedNode.id,
        vendor_name: product?.vendor_name || product?.name || '',
        product_id: product?.id || '',
        layer: 'actual',
        notes: '',
      }, { headers });
      toast.success(`Reassigned to ${product?.name || 'solution'}`);
      setEditAssignOpen(false);
      fetchLayerAssignments();
      if (onRefreshStack) onRefreshStack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reassign');
    }
  };

  const handleRemoveAssignment = async () => {
    if (!window.confirm('Remove this platform assignment? This will unassign the solution from this property.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/assignments`, {
        headers, params: { property_id: selectedProperty, platform_type_id: selectedNode.id, layer: 'actual' },
      });
      toast.success('Assignment removed');
      fetchLayerAssignments();
      if (onRefreshStack) onRefreshStack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  const handleQuickAssign = async (product) => {
    setQuickFillSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/assignments`, {
        property_id: selectedProperty,
        platform_type_id: selectedNode.id,
        vendor_name: product?.vendor_name || product?.name || '',
        product_id: product?.id || '',
        layer: 'actual',
        notes: '',
      }, { headers });
      toast.success(`Assigned ${product?.name || 'solution'} to ${selectedNode.short || selectedNode.name}`);
      setQuickFillOpen(false);
      fetchLayerAssignments();
      if (onRefreshStack) onRefreshStack();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign');
    } finally { setQuickFillSaving(false); }
  };

  return (
    <div className={`${isMobile ? 'w-full h-full' : ''} border-l bg-card/60 backdrop-blur shrink-0 flex flex-col no-print`} style={isMobile ? {} : { width: `${width || 300}px` }} data-testid="node-details-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="text-sm font-semibold">Platform Details</h3>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose}>
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4 animate-fade-in">
          {/* Platform Name */}
          <div>
            <h2 className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {selectedNode.name}
            </h2>
            <div className="flex items-center gap-2 mt-1.5">
              <Badge
                style={{
                  backgroundColor: `${selectedNode.category_color}15`,
                  color: selectedNode.category_color,
                  borderColor: `${selectedNode.category_color}30`,
                }}
                variant="outline"
                className="text-[10px]"
              >
                {selectedNode.category_name}
              </Badge>
              {isPropertyView && (
                <Badge variant={selectedNode.used ? 'default' : 'secondary'} className="text-[10px]">
                  {selectedNode.used ? 'Active' : 'Not Used'}
                </Badge>
              )}
            </div>
          </div>

          {/* Solutions */}
          {(selectedNode.solutions || []).length > 0 ? (
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">
                {selectedNode.solutions.length === 1 ? 'Solution' : `Solutions (${selectedNode.solutions.length})`}
              </p>
              <div className="space-y-1.5">
                {selectedNode.solutions.map((s, i) => (
                  <div key={i} className="p-2 rounded-lg bg-muted/40 text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium flex-1">{s.product_name || s.vendor_name || 'Unknown'}</span>
                      {s.product_status && (
                        <Badge variant="outline" className="text-[10px]">{s.product_status}</Badge>
                      )}
                    </div>
                    {s.vendor_company && s.vendor_company !== s.product_name && (
                      <p className="text-muted-foreground mt-0.5 font-mono">{s.vendor_company}</p>
                    )}
                  </div>
                ))}
              </div>
              {/* Edit/Reassign/Remove buttons for admins in property view */}
              {isPropertyView && isAdmin && (
                <div className="flex gap-1.5 mt-2">
                  <Button size="sm" variant="outline" className="text-xs gap-1 flex-1 h-7" onClick={openEditAssignment} data-testid="reassign-btn">
                    <ArrowLeftRight className="h-3 w-3" /> Reassign
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs gap-1 h-7 text-destructive hover:bg-destructive/10" onClick={handleRemoveAssignment} data-testid="remove-assignment-btn">
                    <Trash2 className="h-3 w-3" /> Remove
                  </Button>
                </div>
              )}
            </div>
          ) : selectedNode.vendor_name ? (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Vendor</p>
              <p className="text-sm font-medium font-mono">{selectedNode.vendor_name}</p>
              {selectedNode.product_name && selectedNode.product_name !== selectedNode.vendor_name && (
                <p className="text-xs text-muted-foreground mt-0.5">Product: {selectedNode.product_name}</p>
              )}
              {selectedNode.product_status && (
                <Badge variant="outline" className="text-[10px] mt-1">{selectedNode.product_status}</Badge>
              )}
              {/* Edit/Reassign/Remove buttons for vendor-only assignments */}
              {isPropertyView && isAdmin && (
                <div className="flex gap-1.5 mt-2">
                  <Button size="sm" variant="outline" className="text-xs gap-1 flex-1 h-7" onClick={openEditAssignment} data-testid="reassign-btn">
                    <ArrowLeftRight className="h-3 w-3" /> Reassign
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs gap-1 h-7 text-destructive hover:bg-destructive/10" onClick={handleRemoveAssignment} data-testid="remove-assignment-btn">
                    <Trash2 className="h-3 w-3" /> Remove
                  </Button>
                </div>
              )}
            </div>
          ) : null}

          {/* Status */}
          {isPropertyView && (
            <div className="flex items-center gap-2">
              {selectedNode.used ? (
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              ) : (
                <XCircle className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-xs text-muted-foreground">
                {selectedNode.used
                  ? `Active at ${stackData?.property?.name || 'this property'}`
                  : `Not used at ${stackData?.property?.name || 'this property'}`}
              </span>
            </div>
          )}

          {/* Quick Fill — shows for unused nodes in property view */}
          {isPropertyView && !selectedNode.used && isAdmin && (
            <div className="rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 dark:bg-blue-950/20 p-3">
              {!quickFillOpen ? (
                <Button
                  onClick={openQuickFill}
                  className="w-full gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                  size="sm"
                  data-testid="quick-fill-btn"
                >
                  <Zap className="h-3.5 w-3.5" /> Quick Fill — Assign a Solution
                </Button>
              ) : quickFillLoading ? (
                <div className="flex items-center justify-center py-3"><Loader2 className="h-5 w-5 animate-spin text-blue-600" /></div>
              ) : (
                <div className="space-y-2">
                  <p className="text-[10px] text-blue-700 dark:text-blue-400 font-semibold uppercase tracking-wider">Choose a product to assign</p>
                  {quickFillProducts.length > 0 ? (
                    <div className="space-y-1 max-h-[200px] overflow-y-auto">
                      {quickFillProducts.map(p => (
                        <button
                          key={p.id}
                          onClick={() => handleQuickAssign(p)}
                          disabled={quickFillSaving}
                          className="w-full text-left p-2 rounded-md border bg-white dark:bg-background hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors text-xs"
                          data-testid={`quick-fill-product-${p.id}`}
                        >
                          <span className="font-medium">{p.name}</span>
                          {p.vendor_name && <span className="text-muted-foreground ml-1">— {p.vendor_name}</span>}
                          {p.status && <Badge variant="outline" className="ml-1.5 text-[8px]">{p.status}</Badge>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-2">
                      <p className="text-xs text-muted-foreground mb-2">No products catalogued for this platform yet.</p>
                      <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => handleQuickAssign({ name: 'TBD', id: '' })}>
                        <Plus className="h-3 w-3" /> Assign as TBD
                      </Button>
                    </div>
                  )}
                  <Button variant="ghost" size="sm" className="w-full text-xs text-muted-foreground" onClick={() => setQuickFillOpen(false)}>Cancel</Button>
                </div>
              )}
            </div>
          )}

          {/* Layer Assignments - only in property view */}
          {isPropertyView && layerAssignments.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Layers className="h-3 w-3" /> Layer Assignments
                  </h4>
                  {isAdmin && missingLayers.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-6 text-[10px] px-2 gap-1"
                      onClick={() => {
                        setAddLayerForm({ layer: missingLayers[0], product_id: '', vendor_name: '' });
                        setShowAddLayer(true);
                      }}
                      data-testid="add-layer-assignment"
                    >
                      <Plus className="h-3 w-3" /> Add Layer
                    </Button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {layerAssignments.map((a, i) => {
                    const meta = LAYER_META[a.layer] || LAYER_META.actual;
                    return (
                      <div key={i} className={`flex items-center gap-2 p-2 rounded-lg text-xs ${meta.bgLight} group`} data-testid={`layer-assignment-${a.layer}`}>
                        <div className={`w-2 h-2 rounded-full ${meta.color} shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">{meta.label}</span>
                          {(a.product_name || a.vendor_name) && (
                            <span className="text-muted-foreground ml-1">
                              — {a.product_name || a.vendor_name}
                            </span>
                          )}
                        </div>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                            onClick={() => handleRemoveLayerAssignment(a.layer)}
                            data-testid={`remove-layer-${a.layer}`}
                          >
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {/* If property view, no assignments, and admin — show add button */}
          {isPropertyView && layerAssignments.length === 0 && isAdmin && (
            <>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Layers className="h-3 w-3" /> Layer Assignments
                  </h4>
                </div>
                <p className="text-xs text-muted-foreground mb-2">Not assigned to any layer at this property.</p>
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-xs gap-1.5"
                  onClick={() => {
                    setAddLayerForm({ layer: 'actual', product_id: '', vendor_name: '' });
                    setShowAddLayer(true);
                  }}
                  data-testid="add-first-layer-assignment"
                >
                  <Plus className="h-3.5 w-3.5" /> Add to Layer
                </Button>
              </div>
            </>
          )}

          <Separator />

          {/* Integrations */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Platform Integrations ({integrations.length})
              </h4>
              {isAdmin && (
                <div className="flex gap-1">
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5 gap-0.5" onClick={() => setShowManualInteg(true)} data-testid="manual-add-integ-btn" title="Select from list">
                    <Plus className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>

            {/* Quick-add integration mode bar */}
            {isAdmin && !quickAddMode && (
              <div className="flex items-center gap-1 mb-2 p-1.5 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-900/30">
                <span className="text-[9px] text-blue-600 font-medium mr-1">Quick add:</span>
                <button onClick={() => startQuickAdd('two-way')} className="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors font-medium" data-testid="quick-add-twoway">↔ Both</button>
                <button onClick={() => startQuickAdd('one-way')} className="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors font-medium" data-testid="quick-add-oneway">→ To</button>
                <button onClick={() => startQuickAdd('one-way-reverse')} className="text-[10px] px-2 py-0.5 rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors font-medium" data-testid="quick-add-from">← From</button>
                <span className="text-[8px] text-blue-400 ml-1">then click a node</span>
              </div>
            )}
            {quickAddMode && (
              <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30 animate-pulse">
                <span className="text-xs text-amber-700 font-medium">
                  Click a platform on the map to connect {quickAddMode === 'two-way' ? '↔' : quickAddMode === 'one-way' ? '→' : '←'} {selectedNode?.short}
                </span>
                <button onClick={() => { setQuickAddMode(null); if (onQuickAddIntegration) onQuickAddIntegration(null); }} className="text-[10px] text-amber-500 hover:text-amber-700">Cancel</button>
              </div>
            )}

            {/* Manual add integration dialog */}
            {showManualInteg && (
              <div className="mb-2 p-2.5 rounded-lg border bg-card space-y-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase">Add Integration</p>
                <div className="flex gap-1.5">
                  {[{v:'two-way',l:'↔ Both'},{v:'one-way',l:'→ To'},{v:'one-way-reverse',l:'← From'}].map(d => (
                    <button key={d.v} onClick={() => setManualIntegDirection(d.v)}
                      className={`text-[10px] px-2 py-1 rounded font-medium transition-colors ${manualIntegDirection === d.v ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
                      {d.l}
                    </button>
                  ))}
                </div>
                <select value={manualIntegTarget} onChange={e => setManualIntegTarget(e.target.value)}
                  className="w-full rounded-md border bg-background px-2 py-1.5 text-xs" data-testid="manual-integ-target">
                  <option value="">Select platform...</option>
                  {(stackData?.nodes || []).filter(n => n.id !== selectedNode?.id).sort((a,b) => a.name.localeCompare(b.name)).map(n => (
                    <option key={n.id} value={n.id}>{n.short || n.name}</option>
                  ))}
                </select>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="ghost" className="flex-1 text-xs h-7" onClick={() => setShowManualInteg(false)}>Cancel</Button>
                  <Button size="sm" className="flex-1 text-xs h-7 gap-1" onClick={addManualIntegration} disabled={!manualIntegTarget} data-testid="manual-integ-save">
                    <Plus className="h-3 w-3" /> Add
                  </Button>
                </div>
              </div>
            )}

            {integrations.length === 0 ? (
              <p className="text-xs text-muted-foreground">No integrations defined</p>
            ) : (
              <div className="space-y-2">
                {outbound.map((link, i) => {
                  const tid = link.target?.id || link.target;
                  const sid = link.source?.id || link.source;
                  const statusColor = link.integration_status === 'gap' ? 'border-l-2 border-l-red-400' :
                    link.level === 'product' && link.integration_status === 'active' ? 'border-l-2 border-l-emerald-500' :
                    link.level === 'product' && link.integration_status === 'possible' ? 'border-l-2 border-l-blue-400' : '';
                  return (
                    <div key={`out-${i}`} className={`flex items-center gap-2 text-xs p-2 rounded-lg bg-muted/40 group ${statusColor}`}>
                      {link.direction === 'two-way' ? (
                        <ArrowLeftRight className="h-3.5 w-3.5 text-foreground/50 shrink-0" />
                      ) : (
                        <ArrowRight className="h-3.5 w-3.5 text-foreground/30 shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{getNodeName(tid)}</span>
                        {link.label && (
                          <span className="text-muted-foreground ml-1">— {link.label}</span>
                        )}
                        {link.integration_status === 'gap' && (
                          <Badge variant="outline" className="ml-1 text-[9px] text-red-500 border-red-200">gap</Badge>
                        )}
                      </div>
                      {isAdmin && isPropertyView && (
                        <Button variant="ghost" size="icon"
                          className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          onClick={() => copyDownToProperty(sid, tid, link.direction)}
                          title="Copy to property integrations"
                          data-testid={`copy-down-${sid}-${tid}`}>
                          <ArrowDownToLine className="h-3 w-3 text-emerald-600" />
                        </Button>
                      )}
                      {role === 'admin' && onDeleteIntegration && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          onClick={() => onDeleteIntegration(sid, tid)}
                          data-testid={`delete-integration-${sid}-${tid}`}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  );
                })}
                {inbound.filter(l => l.direction !== 'two-way').map((link, i) => {
                  const sid = link.source?.id || link.source;
                  const tid = link.target?.id || link.target;
                  return (
                    <div key={`in-${i}`} className="flex items-center gap-2 text-xs p-2 rounded-lg bg-muted/40 group">
                      <ArrowRight className="h-3.5 w-3.5 text-foreground/30 shrink-0 rotate-180" />
                      <div className="flex-1 min-w-0">
                        <span className="font-medium">{getNodeName(sid)}</span>
                        {link.label && (
                          <span className="text-muted-foreground ml-1">— {link.label}</span>
                        )}
                        <span className="text-muted-foreground"> (inbound)</span>
                      </div>
                      {isAdmin && isPropertyView && (
                        <Button variant="ghost" size="icon"
                          className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          onClick={() => copyDownToProperty(sid, tid, link.direction)}
                          title="Copy to property integrations"
                          data-testid={`copy-down-${sid}-${tid}`}>
                          <ArrowDownToLine className="h-3 w-3 text-emerald-600" />
                        </Button>
                      )}
                      {role === 'admin' && onDeleteIntegration && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                          onClick={() => onDeleteIntegration(sid, tid)}
                          data-testid={`delete-integration-${sid}-${tid}`}
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Product-Level Integrations (Tier 2) with Property Confirmation (Tier 3) */}
          {isPropertyView && (stackData?.product_links || []).filter(pl => {
            return pl.source === selectedNode.id || pl.target === selectedNode.id;
          }).length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                  Product Integrations
                </h4>
                <p className="text-[10px] text-muted-foreground mb-2">Vendor-declared connections between products</p>
                <div className="space-y-1.5">
                  {(stackData?.product_links || []).filter(pl => pl.source === selectedNode.id || pl.target === selectedNode.id).map((pl, i) => {
                    const isSource = pl.source === selectedNode.id;
                    const otherProduct = isSource ? pl.to_product_name : pl.from_product_name;
                    return (
                      <div key={i} className={`flex items-center gap-2 text-xs p-2 rounded-lg border-l-2 ${
                        pl.property_confirmed ? 'border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20' :
                        pl.integration_status === 'active' ? 'border-l-blue-500 bg-blue-50/50 dark:bg-blue-950/20' :
                        pl.integration_status === 'possible' ? 'border-l-amber-400 bg-amber-50/50 dark:bg-amber-950/20' :
                        'border-l-slate-300 bg-muted/30'
                      }`}>
                        <ArrowLeftRight className="h-3 w-3 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <span className="font-medium">{otherProduct}</span>
                          {pl.label && <span className="text-muted-foreground ml-1">— {pl.label}</span>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {pl.property_confirmed && (
                            <Badge className="text-[8px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">Live</Badge>
                          )}
                          <Badge variant="outline" className={`text-[9px] ${
                            pl.property_confirmed ? 'text-emerald-700 border-emerald-300' :
                            pl.integration_status === 'active' ? 'text-blue-700 border-blue-300' :
                            pl.integration_status === 'possible' ? 'text-amber-700 border-amber-300' :
                            'text-muted-foreground'
                          }`}>
                            {pl.property_confirmed ? 'confirmed' : pl.integration_status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          {selectedNode.notes && (
            <>
              <Separator />
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Notes</h4>
                <p className="text-xs text-muted-foreground">{selectedNode.notes}</p>
              </div>
            </>
          )}

          {/* Property-Level Integrations (Active Connections) */}
          {isPropertyView && (
            <>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                    <Cable className="h-3 w-3" /> Property Integrations
                  </h4>
                  {isAdmin && (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 gap-1" onClick={() => setShowAddPropInteg(true)} data-testid="add-prop-integration-btn">
                      <Plus className="h-3 w-3" /> Add
                    </Button>
                  )}
                </div>

                {/* Quick-add property integration bar (mirrors platform quick-add) */}
                {isAdmin && !propQuickAddMode && (
                  <div className="flex items-center gap-1 mb-2 p-1.5 rounded-lg bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30" data-testid="prop-quick-add-bar">
                    <span className="text-[9px] text-emerald-700 dark:text-emerald-400 font-medium mr-1">Quick add:</span>
                    <button onClick={() => startPropQuickAdd('two-way')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors font-medium" data-testid="prop-quick-add-twoway">↔ Both</button>
                    <button onClick={() => startPropQuickAdd('one-way')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors font-medium" data-testid="prop-quick-add-oneway">→ To</button>
                    <button onClick={() => startPropQuickAdd('one-way-reverse')} className="text-[10px] px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors font-medium" data-testid="prop-quick-add-from">← From</button>
                    <span className="text-[8px] text-emerald-500 ml-1">then click a node</span>
                  </div>
                )}
                {propQuickAddMode && (
                  <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30 animate-pulse" data-testid="prop-quick-add-active">
                    <span className="text-xs text-amber-700 font-medium">
                      Click a platform on the map to connect {propQuickAddMode === 'two-way' ? '↔' : propQuickAddMode === 'one-way' ? '→' : '←'} {selectedNode?.short}
                    </span>
                    <button onClick={() => { setPropQuickAddMode(null); if (onQuickAddIntegration) onQuickAddIntegration(null); }} className="text-[10px] text-amber-500 hover:text-amber-700">Cancel</button>
                  </div>
                )}

                {/* Existing property integrations */}
                {propIntegrations.length > 0 ? (
                  <div className="space-y-1.5">
                    {propIntegrations.map(pi => {
                      const fromMissing = (!pi.from_product_id || String(pi.from_product_id).startsWith('custom-')) && !!pi.from_platform_id;
                      const toMissing = (!pi.to_product_id || String(pi.to_product_id).startsWith('custom-')) && !!pi.to_platform_id;
                      const anyMissing = fromMissing || toMissing;
                      const rowStyle = anyMissing
                        ? 'bg-amber-50/60 dark:bg-amber-950/20 border-l-2 border-l-amber-500'
                        : 'bg-emerald-50/50 dark:bg-emerald-950/20 border-l-2 border-l-emerald-500';
                      return (
                        <div key={pi.id} className={`flex items-center gap-2 text-xs p-2 rounded-lg group ${rowStyle}`} data-testid={`prop-integ-${pi.id}`}>
                          <ArrowLeftRight className={`h-3 w-3 shrink-0 ${anyMissing ? 'text-amber-600' : 'text-emerald-600'}`} />
                          <div className="flex-1 min-w-0 flex items-center flex-wrap gap-x-1">
                            <span className={`font-medium ${fromMissing ? 'text-amber-700 dark:text-amber-400 italic' : ''}`}>{pi.from_product_name || (fromMissing ? '(no product)' : '')}</span>
                            {fromMissing && isAdmin && (
                              <button
                                onClick={() => openFixProduct(pi, 'from')}
                                className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-amber-200/60 hover:bg-amber-300 text-amber-800 dark:bg-amber-900/40 dark:hover:bg-amber-800 dark:text-amber-200 font-semibold transition-colors"
                                title={`Assign a product for the ${pi.from_platform_id} platform`}
                                data-testid={`fix-prod-from-${pi.id}`}
                              >
                                <Wand2 className="h-2.5 w-2.5" /> fix
                              </button>
                            )}
                            <span className="text-muted-foreground mx-1">↔</span>
                            <span className={`font-medium ${toMissing ? 'text-amber-700 dark:text-amber-400 italic' : ''}`}>{pi.to_product_name || (toMissing ? '(no product)' : '')}</span>
                            {toMissing && isAdmin && (
                              <button
                                onClick={() => openFixProduct(pi, 'to')}
                                className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded bg-amber-200/60 hover:bg-amber-300 text-amber-800 dark:bg-amber-900/40 dark:hover:bg-amber-800 dark:text-amber-200 font-semibold transition-colors"
                                title={`Assign a product for the ${pi.to_platform_id} platform`}
                                data-testid={`fix-prod-to-${pi.id}`}
                              >
                                <Wand2 className="h-2.5 w-2.5" /> fix
                              </button>
                            )}
                            {pi.is_custom && !anyMissing && <Badge variant="outline" className="ml-1 text-[8px]">custom</Badge>}
                            {anyMissing && (
                              <span className="inline-flex items-center gap-0.5 text-[9px] text-amber-700 dark:text-amber-400 ml-1" title="This integration is missing product info on one side">
                                <AlertTriangle className="h-2.5 w-2.5" /> missing product
                              </span>
                            )}
                          </div>
                          {isAdmin && (
                            <Button variant="ghost" size="icon" className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                              onClick={() => removePropInteg(pi.id)} data-testid={`remove-prop-integ-${pi.id}`}>
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No property-level integrations documented yet.</p>
                )}

                {/* Suggestions from product catalog */}
                {isAdmin && propIntegSuggestions.length > 0 && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-amber-600 dark:text-amber-400 font-semibold uppercase tracking-wider flex items-center gap-1">
                        <Lightbulb className="h-3 w-3" /> Suggested ({propIntegSuggestions.length})
                      </span>
                      {propIntegSuggestions.length > 1 && (
                        <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1.5 gap-0.5 text-blue-600" onClick={copyAllSuggestions} data-testid="copy-all-suggestions-btn">
                          <Copy className="h-2.5 w-2.5" /> Copy All
                        </Button>
                      )}
                    </div>
                    <div className="space-y-1 max-h-[150px] overflow-y-auto">
                      {propIntegSuggestions.slice(0, 8).map((sug, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded-lg bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200/50 dark:border-amber-800/30">
                          <div className="flex-1 min-w-0">
                            <span className="font-medium">{sug.from_product_name}</span>
                            <span className="text-muted-foreground mx-0.5">↔</span>
                            <span className="font-medium">{sug.to_product_name}</span>
                          </div>
                          <Button size="sm" variant="ghost" className="h-5 text-[9px] px-1.5 text-emerald-600 shrink-0"
                            onClick={() => addPropIntegFromSuggestion(sug)} data-testid={`add-suggestion-${i}`}>
                            <Plus className="h-2.5 w-2.5" /> Add
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Admin: Assign Products & Properties */}
          {role === 'admin' && (
            <>
              <Separator />
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs gap-1.5"
                onClick={openPlatformCard}
                data-testid="open-platform-card"
              >
                <Plus className="h-3.5 w-3.5" />
                Products & Properties
              </Button>
            </>
          )}

          {/* Node Comments — collaborative thread */}
          {selectedNode && token && (
            <>
              <Separator />
              <NodeComments
                token={token}
                nodeId={selectedNode.id}
                propertyId={selectedProperty}
                isAdmin={isAdmin}
              />
            </>
          )}

          {/* Admin: Navigate to Tech Database */}
          {role === 'admin' && onNavigateToDatabase && (
            <>
              <Separator />
              <Button
                variant="outline"
                size="sm"
                className="w-full text-xs gap-1.5"
                onClick={() => onNavigateToDatabase(selectedNode.id)}
                data-testid="navigate-tech-database"
              >
                <BookOpen className="h-3.5 w-3.5" />
                Manage in Tech Database
              </Button>
            </>
          )}
        </div>
      </ScrollArea>

      {/* Add Layer Assignment Dialog */}
      <Dialog open={showAddLayer} onOpenChange={setShowAddLayer}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="h-4 w-4" /> Add Layer Assignment
            </DialogTitle>
            <DialogDescription>
              Assign {selectedNode?.name} to a layer at {stackData?.property?.name || 'this property'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Layer</Label>
              <Select value={addLayerForm.layer} onValueChange={v => setAddLayerForm(f => ({ ...f, layer: v }))}>
                <SelectTrigger data-testid="layer-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['actual', 'possible', 'ideal']
                    .filter(l => !existingLayers.includes(l))
                    .map(l => (
                      <SelectItem key={l} value={l}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${LAYER_META[l].color}`} />
                          {LAYER_META[l].label}
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Product (optional)</Label>
              {loadingProducts ? (
                <p className="text-xs text-muted-foreground mt-1">Loading products...</p>
              ) : products.length === 0 ? (
                <p className="text-xs text-muted-foreground mt-1">No products for this platform</p>
              ) : (
                <Select
                  value={addLayerForm.product_id || 'none'}
                  onValueChange={v => {
                    const pid = v === 'none' ? '' : v;
                    const prod = products.find(p => p.id === pid);
                    setAddLayerForm(f => ({
                      ...f,
                      product_id: pid,
                      vendor_name: prod?.vendor_name || '',
                    }));
                  }}
                >
                  <SelectTrigger data-testid="product-select">
                    <SelectValue placeholder="Select a product..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No specific product</SelectItem>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} {p.vendor_name ? `(${p.vendor_name})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            {!addLayerForm.product_id && (
              <div>
                <Label className="text-xs">Vendor Name (manual)</Label>
                <Input
                  value={addLayerForm.vendor_name}
                  onChange={e => setAddLayerForm(f => ({ ...f, vendor_name: e.target.value }))}
                  placeholder="Vendor name..."
                  data-testid="vendor-name-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddLayer(false)}>Cancel</Button>
            <Button onClick={handleAddLayerAssignment} data-testid="save-layer-assignment">
              Add to Layer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Solution Dialog */}
      <Dialog open={editAssignOpen} onOpenChange={setEditAssignOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-sm">Reassign Solution</DialogTitle>
            <DialogDescription className="text-xs">
              Choose a different product for <strong>{selectedNode?.name}</strong> at this property.
            </DialogDescription>
          </DialogHeader>
          {editAssignLoading ? (
            <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          ) : (
            <div className="space-y-1 max-h-[300px] overflow-y-auto">
              {editAssignProducts.map(p => (
                <button
                  key={p.id}
                  onClick={() => handleReassign(p)}
                  className="w-full text-left p-2.5 rounded-md border bg-card hover:bg-muted/60 transition-colors text-xs"
                  data-testid={`reassign-product-${p.id}`}
                >
                  <span className="font-medium">{p.name}</span>
                  {p.vendor_name && <span className="text-muted-foreground ml-1.5">— {p.vendor_name}</span>}
                </button>
              ))}
              {editAssignProducts.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">No products found for this platform type.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditAssignOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix Missing Product on Property Integration */}
      <Dialog open={fixProdOpen} onOpenChange={setFixProdOpen}>
        <DialogContent className="max-w-md" data-testid="fix-prod-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Wand2 className="h-4 w-4 text-amber-600" /> Assign Product to Platform
            </DialogTitle>
            <DialogDescription className="text-xs">
              {fixProdInteg && (
                <>Pick the product deployed at this property for the{' '}
                <span className="font-semibold text-foreground">
                  {fixProdSide === 'from' ? fixProdInteg.from_platform_id : fixProdInteg.to_platform_id}
                </span>{' '}
                platform side of this integration.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {fixProdInteg && (
              <div className="text-xs text-muted-foreground p-2 rounded-md bg-muted/40">
                <span className={fixProdSide === 'from' ? 'font-bold text-foreground' : ''}>{fixProdInteg.from_product_name || '(no product)'}</span>
                {' '}↔{' '}
                <span className={fixProdSide === 'to' ? 'font-bold text-foreground' : ''}>{fixProdInteg.to_product_name || '(no product)'}</span>
              </div>
            )}
            {fixProdLoading ? (
              <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" /></div>
            ) : fixProdOptions.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">No products found for this platform.</p>
            ) : (
              <div className="max-h-[300px] overflow-y-auto space-y-1 pr-1">
                {fixProdOptions.map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setFixProdSelected(opt.id)}
                    className={`w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded-md text-left transition-colors ${fixProdSelected === opt.id ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/60 border border-transparent hover:border-border'}`}
                    data-testid={`fix-prod-option-${opt.id}`}
                  >
                    <CheckCircle2 className={`h-3.5 w-3.5 shrink-0 ${fixProdSelected === opt.id ? 'text-primary-foreground' : 'text-transparent'}`} />
                    <span className="flex-1 truncate">{opt.name}</span>
                    {opt.deployed && <Badge variant="outline" className={`text-[8px] ${fixProdSelected === opt.id ? 'border-primary-foreground/50 text-primary-foreground' : ''}`}>deployed</Badge>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFixProdOpen(false)} disabled={fixProdSaving}>Cancel</Button>
            <Button onClick={submitFixProduct} disabled={!fixProdSelected || fixProdSaving} data-testid="fix-prod-save">
              {fixProdSaving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Property Integration Dialog */}
      <Dialog open={showAddPropInteg} onOpenChange={setShowAddPropInteg}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Cable className="h-4 w-4" /> Add Property Integration
            </DialogTitle>
            <DialogDescription className="text-xs">
              Connect two systems at this property. Pick from deployed products or enter a custom system name.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-medium">From System</Label>
              {deployedProducts.length > 0 ? (
                <Select value={addFromProductId || '__custom__'} onValueChange={v => { setAddFromProductId(v === '__custom__' ? '' : v); if (v !== '__custom__') setCustomFromName(''); }}>
                  <SelectTrigger className="mt-1" data-testid="prop-integ-from-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__custom__">Custom / not in catalog</SelectItem>
                    {deployedProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : null}
              {(!addFromProductId || deployedProducts.length === 0) && (
                <Input className="mt-1" value={customFromName} onChange={e => setCustomFromName(e.target.value)} placeholder="System name (e.g., ServiceNow)" data-testid="prop-integ-from-custom" />
              )}
            </div>
            <div>
              <Label className="text-xs font-medium">To System</Label>
              {deployedProducts.length > 0 ? (
                <Select value={addToProductId || '__custom__'} onValueChange={v => { setAddToProductId(v === '__custom__' ? '' : v); if (v !== '__custom__') setCustomToName(''); }}>
                  <SelectTrigger className="mt-1" data-testid="prop-integ-to-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__custom__">Custom / not in catalog</SelectItem>
                    {deployedProducts.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : null}
              {(!addToProductId || deployedProducts.length === 0) && (
                <Input className="mt-1" value={customToName} onChange={e => setCustomToName(e.target.value)} placeholder="System name (e.g., Slack)" data-testid="prop-integ-to-custom" />
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setShowAddPropInteg(false); setCustomFromName(''); setCustomToName(''); setAddFromProductId(''); setAddToProductId(''); }}>Cancel</Button>
            <Button onClick={addCustomPropInteg} data-testid="save-prop-integration-btn">Add Integration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Platform Assignment Card */}
      <Dialog open={showPlatformCard} onOpenChange={setShowPlatformCard}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm">
              <Layers className="h-4 w-4" /> {selectedNode?.name} — Assignments
            </DialogTitle>
            <DialogDescription className="text-xs">
              {platformAssignments.length} properties assigned. {platformProducts.length} products available.
            </DialogDescription>
          </DialogHeader>
          {platformCardLoading ? (
            <div className="py-12 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>
          ) : (
            <>
              {/* Current assignments */}
              <ScrollArea className="flex-1 max-h-[40vh]">
                <div className="space-y-1 p-1">
                  {platformAssignments.length > 0 ? platformAssignments.map(a => {
                    const prop = allProperties.find(p => p.id === a.property_id);
                    const prod = platformProducts.find(p => p.id === a.product_id);
                    return (
                      <div key={a.id || `${a.property_id}-${a.product_id}`} className="flex items-center gap-2 p-2 rounded-lg bg-muted/40 text-xs group" data-testid={`assign-row-${a.property_id}`}>
                        <MapPin className="h-3 w-3 text-muted-foreground shrink-0" />
                        <span className="font-medium flex-1 truncate">{prop?.name || a.property_id}</span>
                        <span className="text-muted-foreground truncate max-w-[120px]">{prod?.name || a.vendor_name || '—'}</span>
                        {isAdmin && (
                          <button onClick={() => removeAssignmentFromCard(a.id)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-red-100" data-testid={`del-assign-${a.property_id}`}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </button>
                        )}
                      </div>
                    );
                  }) : (
                    <p className="text-xs text-muted-foreground text-center py-6">No properties assigned to this platform.</p>
                  )}
                </div>
              </ScrollArea>

              {/* Add new assignment */}
              {isAdmin && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Add Assignment</p>
                  <div className="grid grid-cols-2 gap-2">
                    <select id="assign-prop" className="rounded-md border bg-background px-2 py-1.5 text-xs" data-testid="assign-prop-select">
                      <option value="">Select property...</option>
                      {allProperties
                        .filter(p => !platformAssignments.some(a => a.property_id === p.id))
                        .map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    <select id="assign-prod" className="rounded-md border bg-background px-2 py-1.5 text-xs" data-testid="assign-prod-select">
                      <option value="">Select product...</option>
                      {platformProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <Button size="sm" className="w-full text-xs gap-1 h-7" data-testid="assign-add-btn"
                    onClick={() => {
                      const propEl = document.getElementById('assign-prop');
                      const prodEl = document.getElementById('assign-prod');
                      if (propEl?.value && prodEl?.value) {
                        assignPropertyFromCard(propEl.value, prodEl.value);
                        propEl.value = ''; prodEl.value = '';
                      }
                    }}>
                    <Plus className="h-3 w-3" /> Assign
                  </Button>
                </div>
              )}
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowPlatformCard(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Integration - Missing Products Modal */}
      <Dialog open={showCopyIntegModal} onOpenChange={setShowCopyIntegModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Specify Integration Systems</DialogTitle>
            <DialogDescription>The systems for this integration couldn't be auto-detected. Please specify the product or system name for each side.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-medium">From System ({copyIntegForm.fromPlatformId})</Label>
              <Input value={copyIntegForm.fromName} onChange={e => setCopyIntegForm(p => ({ ...p, fromName: e.target.value }))} placeholder="e.g. Opera PMS" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-medium">To System ({copyIntegForm.toPlatformId})</Label>
              <Input value={copyIntegForm.toName} onChange={e => setCopyIntegForm(p => ({ ...p, toName: e.target.value }))} placeholder="e.g. Duetto RMS" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-medium">Direction</Label>
              <Select value={copyIntegForm.direction} onValueChange={v => setCopyIntegForm(p => ({ ...p, direction: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="two-way">Two-way</SelectItem>
                  <SelectItem value="one-way">One-way</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowCopyIntegModal(false)}>Cancel</Button>
            <Button onClick={submitCopyIntegForm} disabled={!copyIntegForm.fromName || !copyIntegForm.toName}>Add Integration</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
